import { useCallback, useMemo } from 'react'

import { useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import articleParentTitleLink from '~/utils/articleParentTitleLink'

import ArticleAuthor from '../ArticleAuthor/ArticleAuthor'

import styles from './ArticleHeader.module.css'
import EsIndexTypes = indexes.EsIndexTypes
import classNames from 'classnames'
import { format } from 'date-fns'

import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Lozenge from '~/components/atoms/Lozenge/Lozenge'

import ArticleHeaderAuthor from '../ArticleHeaderAuthor/ArticleHeaderAuthor'

export interface ArticleHeaderProps {
  title: string
  subtitle?: string
  headerTableItems?: {
    item: string
    value: string
  }[]
  author?: {
    name: string
    slug: string
    image: any
  }
  reviewedBy?: {
    name: string
    slug: string
    image: any
  }
  /** @deprecated - no longer need to reference override author as author is linked */
  overrideAuthor?: string
  articleType?: string
  articleTypeSlug?: string
  articleTaxonomy?: string
  articleTypeTitle?: string
  /**
   * Suppresses the derived article-type lozenge when `lozenge` is absent. Used
   * by non-article pages, which have no article parent to fall back to.
   */
  hideLozenge?: boolean
  /**
   * Overrides the derived article-type lozenge. Used by non-article pages (e.g.
   * simple reports) whose category links somewhere other than /articles.
   */
  lozenge?: {
    title: string
    link: string
  }
  theme?: ComponentTheme
  verticalPadding?: boolean
  noPaddingBottom?: boolean
  testID?: string
  customTaxonomy?: string
  updatedAt?: string
  publishDate?: string
  readtime?: string
  image: ImageProps & {
    defaultCaption?: {
      artistName: string
      artworkTitle: string
      artworkDate: number
    }
  }
}

export default function ArticleHeader({
  title,
  subtitle,
  author,
  reviewedBy,
  overrideAuthor,
  articleType,
  articleTypeSlug,
  articleTaxonomy,
  articleTypeTitle,
  lozenge,
  hideLozenge,
  theme = ComponentTheme.primary,
  verticalPadding,
  noPaddingBottom,
  headerTableItems,
  testID,
  updatedAt,
  publishDate,
  readtime,
  image,
}: ArticleHeaderProps) {
  const t = useTranslations('articlePage')
  // `hideLozenge` opts out of the derived article-type fallback only — an
  // explicit `lozenge` still renders. Non-article pages set it so a missing
  // lozenge renders nothing instead of an /articles link they have no parent
  // for.
  const articleParentTitle =
    lozenge ??
    (hideLozenge
      ? undefined
      : articleParentTitleLink(
          articleType,
          articleTypeSlug,
          articleTaxonomy,
          articleTypeTitle
        ))

  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const articleTypeFilters = useMemo((): string[] => {
    const filters = []
    if (articleType) filters.push(`articleType:${articleType}`)
    if (articleTypeSlug) filters.push(`articleTypeSlug:${articleTypeSlug}`)
    if (articleTaxonomy) filters.push(`customTaxonomy:${articleTaxonomy}`)
    if (articleTypeTitle) filters.push(`articleTypeTitle:${articleTypeTitle}`)
    return filters
  }, [articleType, articleTypeSlug, articleTaxonomy, articleTypeTitle])

  const onArticleTypeClick = useCallback(() => {
    if (articleTypeFilters.length === 0) return

    sendSearchInsightsEvent(SearchAnalystMethods.clickedFilters, {
      eventName: SearchAnalystEventNames.articleArticleTypeClicked,
      indexType: EsIndexTypes.ARTICLES,
      filters: articleTypeFilters,
    })
  }, [])

  return (
    <Fixture
      theme={theme}
      verticalPadding={verticalPadding}
      noPaddingBottom={noPaddingBottom}
    >
      <div className={styles.imageHeader}>
        <Image asset={image.asset} />
        <Container>
          <div className={styles.posRel}>
            <div className={styles.base} data-testid={testID}>
              <h1 className={styles.title} data-nosnippet>
                <Text
                  size={TextSizes.magPrimaryTitleXLrg}
                  className={styles.mainTitle}
                >
                  {title}
                </Text>
              </h1>
              {subtitle && (
                <>
                  <Text className={styles.subtitle} size={TextSizes.magBodyMed}>
                    {subtitle}
                  </Text>
                </>
              )}

              {/* {headerTableItems && headerTableItems.length > 0 && ( */}
              {/*   <div className={styles.headerTable}> */}
              {/*     {headerTableItems.map((item, index) => ( */}
              {/*       <div key={index} className={styles.headerTableItem}> */}
              {/*         <Text */}
              {/*           size={TextSizes.bodyMed} */}
              {/*           className={styles.headerTableItemTitle} */}
              {/*         > */}
              {/*           {item.item}:{' '} */}
              {/*         </Text> */}
              {/*         <Text */}
              {/*           size={TextSizes.bodyMed} */}
              {/*           className={styles.headerTableItemValue} */}
              {/*         > */}
              {/*           {item.value} */}
              {/*         </Text> */}
              {/*       </div> */}
              {/*     ))} */}
              {/*   </div> */}
              {/* )} */}
            </div>
          </div>
        </Container>
      </div>

      <div className={styles.headerFooterWrap}>
        <Container>
          <div className={styles.headerFooter}>
            <div className={styles.articleMeta}>
              {author && !reviewedBy && <ArticleAuthor author={author} />}
              {author && reviewedBy && (
                <ArticleHeaderAuthor author={author} reviewedBy={reviewedBy} />
              )}

              <div className={styles.publicationInfo}>
                {updatedAt && (
                  <div className={classNames(styles.publicationInfoItem)}>
                    <Text
                      size={TextSizes.bodySml}
                      className={styles.publicationInfoItemTitle}
                    >
                      {t('lastUpdated')}
                    </Text>
                    <Text
                      size={TextSizes.magSecondaryBodySml}
                      className={styles.publicationInfoItemValue}
                    >
                      {format(new Date(updatedAt), 'd MMM yyyy')}
                    </Text>
                  </div>
                )}
                {readtime && (
                  <div className={styles.publicationInfoItem}>
                    <Text
                      size={TextSizes.magSecondaryBodySml}
                      className={styles.publicationInfoItemValue}
                    >
                      {t('minuteRead', { readtime })}
                    </Text>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.tags}>
              {articleParentTitle?.title && (
                <div>
                  <Lozenge
                    title={articleParentTitle.title}
                    hideOnZero={true}
                    link={articleParentTitle.link}
                    onClick={onArticleTypeClick}
                  ></Lozenge>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
    </Fixture>
  )
}
